import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { supportServiceApiClient } from '@src/api/tenant/supportService/supportServiceApiClient';
import { supportServiceCategoryApiClient } from '@src/api/tenant/supportService/supportServiceCategoryApiClient';
import { Pagination } from '@src/api/types/pagination';
import { SupportService } from '@src/api/types/supportService';
import { SupportServiceCategory } from '@src/api/types/supportServiceCategory';
import { brandColors } from '@src/assets/colors/brandColors';
import PinIcon from '@src/assets/images/common/icon/PinIcon';
import PaginationComponent from '@src/components/Pagination';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import { useToast } from '@src/features/toast/context/toastContext';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { TENANT_URL } from '@src/routes/tenant';
import Button from '@src/ui/elements/button/Button';
import InputSearch from '@src/ui/elements/form/InputSearch';
import CellCheckbox from '@src/ui/elements/table/CellCheckbox';
import MoreVertMenu from '@src/ui/elements/table/MoreVertMenu';
import Th from '@src/ui/elements/table/Th';
import Title from '@src/ui/elements/typography/Title';
import ConfirmModal from '@src/ui/templates/Shared/ConfirmModal';
import logger from '@src/utils/logger.ts';
import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

const SupportServicesListForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [supportServices, setSupportServices] = useState<SupportService[]>([]);
  const [pagination, setPagination] = useState<Pagination>();
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [selectedSupportServices, setSelectedSupportServices] = useState<number[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [supportServiceCategories, setSupportServiceCategories] = useState<
    SupportServiceCategory[]
  >([]);
  const [filteredCount, setFilteredCount] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchedKeyword, setSearchedKeyword] = useState<string>('');
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [selectedSupportServiceIdDelete, setSelectedSupportServiceIdDelete] = useState<
    number | null
  >(null);
  const toast = useToast();

  const loadSupportServices = async (
    page = 1,
    sortField: string | null = null,
    sortDirection: 'asc' | 'desc' = 'asc',
    keyword: string | null = null,
  ) => {
    try {
      const params: { page: number; sort?: string; direction?: 'asc' | 'desc'; keyword?: string } =
        { page };
      if (sortField) {
        params.sort = sortField;
        params.direction = sortDirection;
      }
      if (keyword) {
        params.keyword = keyword;
      }
      const res = await supportServiceApiClient.index(params);
      handleApiResponse(res, (data) => {
        setSupportServices(data.data);
        setPagination(data.pagination);
        setFilteredCount(data.pagination?.total || 0);
        setSearchedKeyword(keyword || '');
      });
    } catch (error) {
      console.error('Failed to load support services:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadSupportServiceCategories = async () => {
      const res = await supportServiceCategoryApiClient.index();
      handleApiResponse(res, (data) => {
        setSupportServiceCategories(data);
      });
    };

    loadSupportServiceCategories();
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const page = parseInt(query.get('page') || '1', 10);
    const sort = query.get('sort') || 'published_at';
    const direction = (query.get('direction') as 'asc' | 'desc') || 'desc';
    const keyword = query.get('keyword');
    setSortField(sort);
    setSearchValue(keyword || '');
    setSortOrder(direction || 'asc');
    loadSupportServices(page, sort, direction || 'asc', keyword);
  }, [location.search]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown-button') && !target.closest('.dropdown-content')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handlePageChange = (page: number) => {
    const currentPage = new URLSearchParams(location.search).get('page');
    if (currentPage === page.toString()) {
      return;
    }
    const query = new URLSearchParams(location.search);
    query.set('page', page.toString());
    setLoading(true);
    navigate({ search: query.toString() });
  };

  const handleSortClick = (field: string) => {
    const newSortOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newSortOrder);
    const query = new URLSearchParams(location.search);
    query.set('sort', field);
    query.set('direction', newSortOrder);
    query.set('page', '1');
    setLoading(true);
    navigate({ search: query.toString() });
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleSearchSubmit = () => {
    const query = new URLSearchParams(location.search);
    const currentKeyword = query.get('keyword');
    const newKeyword = searchValue.trim();

    // 同じキーワードでの連続検索を防ぐ
    if (currentKeyword === newKeyword) {
      return;
    }

    logger.log('Search query:', query);
    setLoading(true);
    query.set('keyword', newKeyword);
    query.set('page', '1'); // キーワード検索時はページを1にリセット
    navigate({ search: query.toString() });
  };

  const handleChangeThCheckbox = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const currentSupportServiceIds = supportServices.map((supportService) => supportService.id);
    if (event.target.checked) {
      // 現在表示されているページの記事をすべて選択
      setSelectedSupportServices((prevSelected) => [
        ...new Set([...prevSelected, ...currentSupportServiceIds]),
      ]);
    } else {
      // 現在表示されているページの記事の選択を解除
      setSelectedSupportServices((prevSelected) =>
        prevSelected.filter((id) => !currentSupportServiceIds.includes(id)),
      );
    }
  };

  const handleChangeTdCheckbox = async (
    event: React.ChangeEvent<HTMLInputElement>,
    supportServiceId: number,
  ) => {
    if (event.target.checked) {
      setSelectedSupportServices([...selectedSupportServices, supportServiceId]);
    } else {
      setSelectedSupportServices(selectedSupportServices.filter((id) => id !== supportServiceId));
    }
  };

  const handleSupportServiceCategoryChange = async (supportServiceCategoryId: number) => {
    setLoading(true);
    await supportServiceApiClient.bulkUpdateCategory({
      support_service_ids: selectedSupportServices,
      support_service_category_id: supportServiceCategoryId,
    });
    await loadSupportServices(
      pagination?.current_page || 1,
      sortField,
      sortOrder || 'asc',
      searchValue,
    );
    setSelectedSupportServices([]);
    toast(TOAST_MESSAGE.UPDATE);
    setLoading(false);
    setIsDropdownOpen(false);
  };

  const handleOpenDeleteModal = (supportServiceId: number) => {
    setSelectedSupportServiceIdDelete(supportServiceId);
    setIsConfirmDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setSelectedSupportServiceIdDelete(null);
    setIsConfirmDeleteModalOpen(false);
  };

  const handleDelete = async (supportServiceId: number | null) => {
    setLoading(true);
    try {
      if (!supportServiceId) {
        return;
      }

      await supportServiceApiClient.delete(supportServiceId);

      const currentPage = new URLSearchParams(location.search).get('page') || '1';
      await loadSupportServices(
        parseInt(currentPage, 10),
        sortField,
        sortOrder || 'asc',
        searchValue,
      );
      toast(TOAST_MESSAGE.DELETE);
    } catch (error) {
      console.error('Failed to delete support service:', error);
    } finally {
      setLoading(false);
      setIsConfirmDeleteModalOpen(false);
    }
  };

  return (
    <React.Fragment>
      <main>
        <SpinnerOverlay loading={loading} />
        <div className='flex flex-col'>
          <div className='flex items-center justify-between'>
            <Title>支援サービス一覧</Title>
            <div className='w-[250px]'>
              <InputSearch
                key={searchedKeyword}
                value={searchValue}
                defaultValue={searchedKeyword}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                onSubmit={handleSearchSubmit}
                onClear={() => {
                  setSearchValue('');
                  navigate({ search: '' });
                }}
              />
            </div>
          </div>
          <div className='mt-4 flex items-center justify-between'>
            <div className='w-[172px]'>
              <div className='relative'>
                {selectedSupportServices.length > 0 && (
                  <button
                    className='dropdown-button flex w-full cursor-pointer appearance-none items-center justify-between gap-4 rounded border border-solid border-primary-500 px-5 py-3 text-sm text-primary-500 focus:outline-none'
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <span>カテゴリの変更</span>
                    <div className='h-[5px] w-[10px] bg-primary-500 [clip-path:polygon(50%_100%,0_0,100%_0)]'></div>
                  </button>
                )}
                {isDropdownOpen && (
                  <div className='dropdown-content absolute left-0 top-[102%] z-10 w-[240px]'>
                    <div className='flex flex-col gap-px overflow-hidden rounded border border-solid border-gray-200 bg-gray-200'>
                      {supportServiceCategories.map((category) => (
                        <button
                          key={category.id}
                          type='button'
                          className='block select-none bg-white px-6 py-3 text-left text-sm transition-colors hover:bg-primary-500 hover:text-white'
                          onClick={() => handleSupportServiceCategoryChange(category.id)}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <Button
              size='s'
              onClick={() => {
                navigate(TENANT_URL.SUPPORT_SERVICES_CREATE);
              }}
            >
              新規作成
            </Button>
          </div>
          {searchedKeyword && (
            <p className='overflow-wrap-anywhere mt-8 text-sm'>
              「{searchedKeyword}」で絞り込んだ結果：{filteredCount}件
            </p>
          )}
          <div className='mt-8 overflow-auto'>
            <table className='size-full min-w-[900px] table-fixed text-sm [&_tbody_tr>*]:p-0 [&_thead_th]:p-0'>
              <colgroup>
                <col className='w-[42px]' />
                <col className='w-[132px]' />
                <col className='w-[24px]' />
                <col className='w-full' />
                <col className='w-[132px]' />
                <col className='w-[144px]' />
                <col className='w-[144px]' />
                <col className='w-[46px]' />
              </colgroup>
              <thead className='h-full border border-solid border-gray-200 bg-gray-50'>
                <tr>
                  <th>
                    <CellCheckbox
                      checked={
                        supportServices.length > 0 &&
                        supportServices.every((supportService) =>
                          selectedSupportServices.includes(supportService.id),
                        )
                      }
                      onChange={(e) => handleChangeThCheckbox(e)}
                    />
                  </th>
                  <Th
                    sortable
                    sortOrder={sortOrder && sortField === 'published_at' ? sortOrder : null}
                    onClick={() => handleSortClick('published_at')}
                  >
                    公開日
                  </Th>
                  <Th>{''}</Th>
                  <Th>支援サービス名</Th>
                  <Th
                    sortable
                    sortOrder={
                      sortOrder && sortField === 'tenantUser.fullname_kana' ? sortOrder : null
                    }
                    onClick={() => handleSortClick('tenantUser.fullname_kana')}
                  >
                    作成者
                  </Th>
                  <Th
                    sortable
                    sortOrder={
                      sortOrder && sortField === 'supportServiceCategory.name' ? sortOrder : null
                    }
                    onClick={() => handleSortClick('supportServiceCategory.name')}
                  >
                    カテゴリ
                  </Th>
                  <Th
                    sortable
                    sortOrder={sortOrder && sortField === 'status' ? sortOrder : null}
                    onClick={() => handleSortClick('status')}
                  >
                    ステータス
                  </Th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {supportServices.length > 0
                  ? supportServices.map((supportService) => (
                      <tr
                        key={supportService.id}
                        className='[&:nth-child(2n)]:bg-gray-10'
                      >
                        <td>
                          <CellCheckbox
                            checked={selectedSupportServices.includes(supportService.id)}
                            onChange={(e) => handleChangeTdCheckbox(e, supportService.id)}
                          />
                        </td>
                        <td>
                          <div className='px-4 py-6'>{supportService.published_at}</div>
                        </td>
                        <td>
                          <div className='flex items-center justify-center py-6'>
                            {supportService.is_top_display && (
                              <PinIcon
                                color={brandColors['500']}
                                size={20}
                              />
                            )}
                          </div>
                        </td>
                        <td>
                          <div className='truncate px-4 py-6'>{supportService.title}</div>
                        </td>
                        <td
                          title={`${supportService.tenant_user.family_name} ${supportService.tenant_user.first_name}`}
                        >
                          <div className='truncate px-4 py-6'>{`${supportService.tenant_user.family_name} ${supportService.tenant_user.first_name}`}</div>
                        </td>
                        <td>
                          <div className='truncate px-4 py-6'>
                            {supportService.support_service_category.name}
                          </div>
                        </td>
                        <td>
                          <div className='px-4 py-6'>{supportService.status}</div>
                        </td>
                        <td>
                          <div className='grid h-full place-items-center'>
                            <MoreVertMenu
                              menuItems={[
                                {
                                  label: '編集する',
                                  onClick: () => {
                                    navigate(
                                      generatePath(TENANT_URL.SUPPORT_SERVICES_EDIT, {
                                        id: supportService.id,
                                      }),
                                    );
                                  },
                                },
                                {
                                  label: '削除する',
                                  variant: 'warning',
                                  onClick: () => handleOpenDeleteModal(supportService.id),
                                },
                              ]}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  : null}
              </tbody>
            </table>
            {!searchedKeyword && pagination?.total === 0 ? (
              <p className='mt-4 text-sm'>データがありません。</p>
            ) : null}
          </div>
          {/* ページング */}
          {pagination && pagination.total !== 0 && (
            <div className='mt-6'>
              <PaginationComponent
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </main>
      <ConfirmModal
        title='完全に削除しますか？'
        message='支援サービスが完全に削除されますがよろしいですか？'
        confirmText='完全に削除'
        cancelText='戻る'
        isOpen={isConfirmDeleteModalOpen}
        onConfirm={() => handleDelete(selectedSupportServiceIdDelete)}
        onClose={handleCloseDeleteModal}
        isDanger={true}
      />
    </React.Fragment>
  );
};

export default SupportServicesListForm;
